import { FieldName } from 'react-hook-form';

import { AdditionalEntitlement } from '@dreryk/egabinet-prescription-frontend-api';

import { commonTranslationKeys, createTranslatedText, TranslatableText } from '@dreryk/react-common';
import { Multiselect } from '@dreryk/react-components';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';

const triggerLabelExtractor = (selectedAdditionalEntitlements: AdditionalEntitlement[]): string => {
   return selectedAdditionalEntitlements.map((selectedValue) => selectedValue.additionalEntitlementType).join(', ');
};

type BeanWithAdditionalEntitlements<NAME extends string> = { [key in NAME]: AdditionalEntitlement[] | null };

export type AdditionalEntitlementMultiSelectProps<NAME extends string> = Pick<
   DraftPrescriptionsFormController,
   'register'
> & {
   readonly name: FieldName<BeanWithAdditionalEntitlements<NAME>>;
   readonly defaultValue?: AdditionalEntitlement[] | null;
   readonly additionalEntitlements: AdditionalEntitlement[];
};

const MAX_ADDITIONAL_ENTITLEMENTS = 3;
const validateMaxItems = (value: unknown): TranslatableText | undefined => {
   const valueCasted = value as AdditionalEntitlement[];
   if (valueCasted.length > MAX_ADDITIONAL_ENTITLEMENTS) {
      return prescriptionTranslationKeys.maxEntitlementsExceeded;
   }
   return undefined;
};

export const AdditionalEntitlementMultiSelect = <NAME extends string>(
   props: AdditionalEntitlementMultiSelectProps<NAME>
): JSX.Element => {
   return (
      <Multiselect<AdditionalEntitlement>
         controller={props.register(props.name, { validate: validateMaxItems })}
         defaultValue={props.defaultValue}
         itemKeyGenerator={'additionalEntitlementType'}
         triggerLabelExtractor={triggerLabelExtractor}
         placeholder={commonTranslationKeys.choose}
         compact
         label={prescriptionTranslationKeys.entitlements}
         maxHeight={190}
         testId={'additionalEntitlements'}
      >
         <>
            <Multiselect.SelectAll<AdditionalEntitlement>
               testId={'selectAll'}
               items={props.additionalEntitlements}
               selectAllLabel={commonTranslationKeys.choose}
            />
            <Multiselect.List<AdditionalEntitlement>
               items={props.additionalEntitlements}
               keyExtractor={'additionalEntitlementType'}
            >
               {(entitlement) => (
                  <Multiselect.Item<AdditionalEntitlement>
                     item={entitlement}
                     label={createTranslatedText(entitlement.additionalEntitlementType)}
                     testId={entitlement.additionalEntitlementType}
                  />
               )}
            </Multiselect.List>
         </>
      </Multiselect>
   );
};
